import i18next from 'i18next';
import { DateFormatter } from 'infrastructure/functions';
import { DateFormats } from 'infrastructure/enums';
import { useAppDispatch } from 'store';
import { notificationController } from 'infrastructure/antd/controllers/notification-controller';
import { useTranslation } from 'react-i18next';

import { DragHandle } from './table-row';
import TableCheckbox from '../table-checkbox';
import NoteCell from '../note-cell';

import type {
  IQueueTableData,
  ITableNote,
  TTableName,
} from 'infrastructure/interfaces';
import type { IBaseTableColumns } from 'components/atoms/base-table/types';

const tableNameMap: Record<TTableName, string> = {
  'follow-up': i18next.t('labels.followUp'),
  newly: i18next.t('labels.newly'),
  partials: i18next.t('labels.partials'),
};

export const useColumns = (): IBaseTableColumns<IQueueTableData> => {
  const {
    ccmConsole: { removeFromQueue, setTableNote, toggleOpenDrawer },
  } = useAppDispatch();
  const { t } = useTranslation();

  return [
    {
      label: t('labels.patientInfo'),
      key: 'name',
      width: '280px',
      render(name: string, record) {
        return <DragHandle name={name} guid={record.guid} />;
      },
      dataCy: 'queue-table-date',
    },
    {
      label: t('labels.dueDate'),
      key: 'dueDate',
      width: '95px',
      align: 'center',
      render(dueDate: string) {
        return DateFormatter({
          date: dueDate,
          format: DateFormats['MMM d, yyyy'],
        });
      },
    },
    {
      label: 'Table',
      width: '70px',
      key: 'tableName',
      align: 'center',
      render(tableName: TTableName) {
        return tableNameMap[tableName];
      },
    },
    {
      label: t('labels.notes'),
      width: '360px',
      key: 'note',
      render(note: ITableNote | null, record) {
        return (
          <NoteCell
            text={note?.text ?? ''}
            onClick={() => {
              setTableNote({
                note,
                tableName: record.tableName,
                patientGuid: record.guid,
                patientName: record.name,
              });
              toggleOpenDrawer();
            }}
          />
        );
      },
    },
    {
      label: t('labels.completed'),
      width: '70px',
      key: 'guid',
      align: 'center',
      render(guid: string, record) {
        return (
          <TableCheckbox
            dataCy="queue-completed-checkbox"
            onClick={() => {
              removeFromQueue({ guid, tableName: record.tableName });
              notificationController.success({
                message: t('messages.patientRemovedFromQueue'),
              });
            }}
            actionType="removeFromQueue"
            patientGuid={record.guid}
          />
        );
      },
    },
  ];
};
